(() => {
  const get = (id) => document.getElementById(id);
  if (!get('rawInput')) return;
  const hex = (value) => value.toString(16).toUpperCase().padStart(2, '0');
  const integer = (value) => /^(?:\d+|0x[0-9a-f]+)$/i.test(value.trim()) ? Number(value.trim()) : NaN;
  const inRange = (value, low, high) => Number.isSafeInteger(value) && value >= low && value <= high;

  function crc16(bytes) {
    let crc = 0xFFFF;
    for (const byte of bytes) {
      crc ^= byte;
      for (let bit = 0; bit < 8; bit++) crc = crc & 1 ? (crc >>> 1) ^ 0xA001 : crc >>> 1;
    }
    return [crc & 0xFF, crc >>> 8];
  }

  window.handleCalcInput = (field) => {
    const text = field.value.trim();
    const chunks = text.split(/\s+/);
    const valid = text !== '' && chunks.every((part) => /^[0-9a-f]+$/i.test(part) && part.length % 2 === 0);
    const raw = chunks.join('');
    get('crcResult').textContent = '-- --';
    get('lrcResult').textContent = '--';
    get('dissectorUI').style.display = 'none';
    get('dissectorContent').replaceChildren();
    field.setAttribute('aria-invalid', String(text !== '' && (!valid || raw.length > 508)));
    if (!valid || raw.length > 508) {
      get('inputMsg').textContent = text === '' ? '' : 'Enter complete HEX bytes (00–FF), optionally separated by spaces; maximum 254 bytes. Do not include CRC.';
      return;
    }
    get('inputMsg').textContent = '';
    const bytes = raw.match(/../g).map((part) => parseInt(part, 16));
    const crc = crc16(bytes).map(hex).join(' ');
    get('crcResult').textContent = crc;
    get('lrcResult').textContent = hex((-bytes.reduce((sum, byte) => sum + byte, 0)) & 0xFF);
    const rows = [
      ['Slave Address', bytes.slice(0, 1).map(hex).join(' ')],
      ['Function Code', bytes.slice(1, 2).map(hex).join(' ')],
      ['Data / Parameters', bytes.slice(2).map(hex).join(' ')],
      ['CRC Checksum', crc]
    ];
    for (const [label, value] of rows) {
      if (!value) continue;
      const row = document.createElement('div');
      row.className = 'dissector-row';
      const data = document.createElement('div');
      data.className = 'd-byte';
      data.textContent = value;
      const description = document.createElement('div');
      description.className = 'd-desc';
      description.textContent = label;
      row.append(data, description);
      get('dissectorContent').append(row);
    }
    get('dissectorUI').style.display = 'block';
  };

  function builderError(message) {
    get('builderMsg').textContent = message;
    get('frameOutput').textContent = '-- --';
    get('copyFrame').disabled = true;
  }

  window.updateBuilder = () => {
    const slave = integer(get('slaveId').value);
    const func = parseInt(get('funcCode').value, 16);
    const addr = integer(get('startAddr').value);
    const qty = integer(get('quantity').value);
    const single = func === 6;
    const multiple = func === 16;
    const max = single ? 65535 : multiple ? 123 : func === 1 || func === 2 ? 2000 : 125;
    get('qtyLabel').textContent = single ? 'Register Value (Decimal)' : 'Quantity (Length)';
    get('quantity').min = single ? '0' : '1';
    get('quantity').max = String(max);
    get('registerValuesGroup').hidden = !multiple;
    if (![1, 2, 3, 4, 6, 16].includes(func)) return builderError('Select a supported function code.');
    if (!inRange(slave, 1, 247)) return builderError('Slave address must be an integer from 1 to 247.');
    if (!inRange(addr, 0, 65535)) return builderError('Start address must be 0–65535 (decimal or 0x-prefixed HEX).');
    if (!inRange(qty, single ? 0 : 1, max)) return builderError(single ? 'Register value must be an integer from 0 to 65535.' : `Quantity must be an integer from 1 to ${max}.`);
    if (!single && addr + qty > 65536) return builderError('Address plus quantity exceeds the register or coil address range.');
    const bytes = [slave, func, addr >>> 8, addr & 0xFF, qty >>> 8, qty & 0xFF];
    if (multiple) {
      const text = get('registerValues').value.trim();
      const tokens = text.split(/[\s,]+/);
      const values = tokens.map(integer);
      if (!text || !values.every((value) => inRange(value, 0, 65535))) return builderError('Enter register values from 0 to 65535, separated by spaces or commas. HEX values need a 0x prefix.');
      if (values.length !== qty) return builderError(`Enter exactly ${qty} register value${qty === 1 ? '' : 's'} to match Quantity.`);
      bytes.push(values.length * 2);
      for (const value of values) bytes.push(value >>> 8, value & 0xFF);
    }
    get('builderMsg').textContent = '';
    get('frameOutput').textContent = [...bytes, ...crc16(bytes)].map(hex).join(' ');
    get('copyFrame').disabled = false;
  };

  window.clearInput = (id) => {
    get(id).value = '';
    window.handleCalcInput(get(id));
  };
  window.copyText = async (id) => {
    const button = get('copyFrame');
    if (button.disabled) return;
    try {
      await navigator.clipboard.writeText(get(id).textContent);
      button.textContent = 'Copied!';
    } catch {
      button.textContent = 'Copy failed — select HEX';
    }
    setTimeout(() => { button.textContent = 'Copy HEX'; }, 2500);
  };

  const snippets = {
    python: `def crc16(data: bytes) -> bytes:
    crc = 0xFFFF
    for byte in data:
        crc ^= byte
        for _ in range(8):
            crc = (crc >> 1) ^ 0xA001 if crc & 1 else crc >> 1
    return crc.to_bytes(2, 'little')`,
    c: `#include <stdint.h>
uint16_t crc16(const uint8_t *data, uint16_t length) {
    uint16_t crc = 0xFFFF;
    for (uint16_t i = 0; i < length; i++) {
        crc ^= data[i];
        for (int bit = 0; bit < 8; bit++)
            crc = (crc & 1) ? (crc >> 1) ^ 0xA001 : crc >> 1;
    }
    return crc; // Transmit low byte first, then high byte.
}`,
    go: `func CRC16(data []byte) uint16 {
    var crc uint16 = 0xFFFF
    for _, b := range data {
        crc ^= uint16(b)
        for i := 0; i < 8; i++ {
            if crc&1 != 0 { crc = (crc >> 1) ^ 0xA001 } else { crc >>= 1 }
        }
    }
    return crc // Transmit low byte first, then high byte.
}`
  };
  window.switchLang = (lang, button) => {
    document.querySelectorAll('.modbus-expert-section .l-btn').forEach((el) => el.classList.remove('active'));
    button.classList.add('active');
    get('codeSnippet').textContent = snippets[lang];
  };
  window.switchTab = (id, button) => {
    document.querySelectorAll('.modbus-expert-section .t-btn').forEach((el) => {
      el.classList.remove('active');
      el.setAttribute('aria-selected', 'false');
    });
    document.querySelectorAll('.modbus-expert-section .tab-pane').forEach((el) => el.classList.remove('active'));
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');
    get('tab-' + id).classList.add('active');
    if (id === 'code') window.switchLang('python', document.querySelector('.modbus-expert-section .l-btn'));
  };
  const tabs = [...document.querySelectorAll('.modbus-expert-section .t-btn')];
  tabs.forEach((tab, index) => {
    tab.addEventListener('keydown', (event) => {
      let target;
      if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') target = (index + tabs.length - 1) % tabs.length;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = tabs.length - 1;
      if (target === undefined) return;
      event.preventDefault();
      tabs[target].focus();
      window.switchTab(tabs[target].getAttribute('aria-controls').replace('tab-', ''), tabs[target]);
    });
  });
  window.updateBuilder();
})();
